import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { catchError, filter, map, of, take } from 'rxjs';
import { TherapyPlanStateService } from '@therapist/shared/services/state/therapy-plan-state.service';
import { TherapyPlanDto } from '@therapist/shared/models/therapy-plan/therapy-plan.dto';
import { GuidCheckerUtil } from '@base/utils/guid-checker.util';

export const therapistTherapyPlanReadyGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const therapyPlanState = inject(TherapyPlanStateService);
  const router = inject(Router);

  const clientId = route.paramMap.get('clientId');
  const therapyPlanId = route.paramMap.get('therapyPlanId');

  if (!clientId || !therapyPlanId) {
    console.warn('therapistTherapyPlanReadyGuard: No clientId or therapyPlanId in route, redirecting to 404');
    return router.createUrlTree(['/404']);
  }

  if (!GuidCheckerUtil.isValidGuid(clientId)) {
    console.warn('therapistTherapyPlanReadyGuard: ClientId in route is no valid Guid, redirecting to 404');
    return router.createUrlTree(['/404']);
  }

  if (!GuidCheckerUtil.isValidGuid(therapyPlanId)) {
    console.warn('therapistTherapyPlanReadyGuard: TherapyPlanId in route is no valid Guid, redirecting to 404');
    return router.createUrlTree(['/404']);
  }

  const selectedTherapyPlan = therapyPlanState.getSelectedTherapyPlan();
  const cachedTherapyPlan = selectedTherapyPlan();

  // Schon der richtige Client im State? Sofort durchlassen.
  if (cachedTherapyPlan?.id === therapyPlanId && therapyPlanState.isTherapyPlanStateInSyncWithClientId(clientId)) {
    return true;
  }

  // Laden anstoßen und warten, bis genau dieser Client im State steht.
  therapyPlanState.loadTherapyPlanById(therapyPlanId);

  return toObservable(selectedTherapyPlan).pipe(
    filter((therapyPlan): therapyPlan is TherapyPlanDto => !!therapyPlan && therapyPlan.id === therapyPlanId),
    take(1),
    map(() => true),
    // Falls Laden fehlschlägt → 404
    catchError(() => {
      console.warn('therapistTherapyPlanReadyGuard: Loading therapyPlan failed, redirecting to 404');
      return of(router.createUrlTree(['/404']));
    })
  );
};
